import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import type { z } from 'zod';
import { tokenStore } from '@/auth/token-store';
import { itemEnvelope, pageEnvelope, type Page, type PageParams } from './envelope';
import { ApiError, ErrorCode } from './errors';
import { apiRequest, type QueryParams } from './http';

export type ListParams = PageParams &
  Record<string, string | number | boolean | null | undefined | readonly string[]>;

export interface Identified {
  id: string;
}

/**
 * How a resource resolves ids:
 * - `ids`: `GET /<path>?ids[]=…` batches and `GET /<path>/{id}` (the default);
 * - `list`: small reference collections the API serves whole and unpaginated
 *   (tax rates, meters, features …) — fetch the list once and pick from it.
 */
export type LookupStrategy = 'ids' | 'list';

/**
 * A REST collection: its cache keys (scoped to tenant + environment) and
 * its typed reads. Writes and state transitions live in the resource's own
 * module because their bodies differ per resource.
 */
export interface Resource<T extends Identified> {
  name: string;
  path: string;
  schema: z.ZodType<T>;
  lookup: LookupStrategy;
  /**
   * Whether a cached row has the detail shape. List rows (and some command
   * results) omit nested collections such as `lines`; a detail read that
   * finds only such a row shows it at once and refetches the full one.
   */
  isComplete(row: T): boolean;
  keys: {
    all: () => readonly unknown[];
    lists: () => readonly unknown[];
    list: (params: ListParams) => readonly unknown[];
    details: () => readonly unknown[];
    detail: (id: string) => readonly unknown[];
  };
  api: {
    list: (params?: ListParams, signal?: AbortSignal) => Promise<Page<T>>;
    get: (id: string, signal?: AbortSignal) => Promise<T>;
    /** Batch lookup; for `list` resources this returns the whole collection. */
    getMany: (ids: readonly string[], signal?: AbortSignal) => Promise<T[]>;
  };
}

function notFound(name: string, path: string, id: string): ApiError {
  return new ApiError({
    status: 404,
    code: ErrorCode.ResourceNotFound,
    title: 'The resource does not exist.',
    detail: `No such ${name}: ${id}.`,
    method: 'GET',
    path: `${path}/${id}`,
  });
}

export function defineResource<T extends Identified>(
  name: string,
  path: string,
  schema: z.ZodType<T>,
  options: { lookup?: LookupStrategy; isComplete?: (row: T) => boolean } = {},
): Resource<T> {
  const lookup = options.lookup ?? 'ids';
  const isComplete = options.isComplete ?? (() => true);
  const item = itemEnvelope(schema);
  const page = pageEnvelope(schema);
  const all = () => [tokenStore.scopeId(), name] as const;

  const list = async (params?: ListParams, signal?: AbortSignal): Promise<Page<T>> =>
    apiRequest(page, path, { query: params as QueryParams | undefined, signal });

  /** Every row of a small collection (follows cursors defensively). */
  const everything = async (signal?: AbortSignal): Promise<T[]> => {
    const rows: T[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < 20; i++) {
      const result: Page<T> = await list({ limit: 100, cursor }, signal);
      rows.push(...result.data);
      if (!result.has_more || !result.next_cursor) break;
      cursor = result.next_cursor;
    }
    return rows;
  };

  return {
    name,
    path,
    schema,
    lookup,
    isComplete,
    keys: {
      all,
      lists: () => [...all(), 'list'] as const,
      list: (params) => [...all(), 'list', params] as const,
      details: () => [...all(), 'detail'] as const,
      detail: (id) => [...all(), 'detail', id] as const,
    },
    api: {
      list,
      get:
        lookup === 'ids'
          ? async (id, signal) =>
              (await apiRequest(item, `${path}/${encodeURIComponent(id)}`, { signal })).data
          : async (id, signal) => {
              const found = (await everything(signal)).find((row) => row.id === id);
              if (!found) throw notFound(name, path, id);
              return found;
            },
      getMany:
        lookup === 'ids'
          ? async (ids, signal) =>
              (await apiRequest(page, path, { query: { ids, limit: Math.min(ids.length, 100) }, signal }))
                .data
          : (_ids, signal) => everything(signal),
    },
  };
}

/** Put fetched rows into the per-id cache so hydration and detail pages start warm. */
export function seedDetails<T extends Identified>(
  client: QueryClient,
  resource: Resource<T>,
  rows: readonly T[],
): void {
  for (const row of rows) {
    const key = resource.keys.detail(row.id);
    if (client.getQueryData(key) === undefined) client.setQueryData(key, row);
  }
}

export interface ListQueryOptions {
  enabled?: boolean;
  /** Rows per page (API max 100). */
  pageSize?: number;
}

/** Cursor-paginated list with "load more"; rows are flattened for tables. */
export function useResourceList<T extends Identified>(
  resource: Resource<T>,
  params: ListParams = {},
  options: ListQueryOptions = {},
) {
  const client = useQueryClient();
  const pageSize = options.pageSize ?? 50;
  const query = useInfiniteQuery({
    queryKey: resource.keys.list({ ...params, limit: pageSize }),
    queryFn: async ({ pageParam, signal }) => {
      const result = await resource.api.list({ ...params, limit: pageSize, cursor: pageParam }, signal);
      seedDetails(client, resource, result.data);
      return result;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more && last.next_cursor ? last.next_cursor : undefined),
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.data) ?? [], [query.data]);
  return { ...query, rows };
}

/** A whole small collection (tax rates, meters, features …) for pickers and lookups. */
export function useResourceAll<T extends Identified>(
  resource: Resource<T>,
  options: { enabled?: boolean } = {},
) {
  const client = useQueryClient();
  return useQuery({
    queryKey: [...resource.keys.lists(), 'all'],
    queryFn: async ({ signal }) => {
      const rows = await resource.api.getMany([], signal);
      seedDetails(client, resource, rows);
      return rows;
    },
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });
}

const DETAIL_STALE_MS = 30_000;

export function useResourceItem<T extends Identified>(
  resource: Resource<T>,
  id: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: resource.keys.detail(id ?? ''),
    queryFn: ({ signal }) => resource.api.get(id ?? '', signal),
    enabled: Boolean(id) && (options.enabled ?? true),
    // A row seeded from a list is shown immediately but is stale until the detail read lands.
    staleTime: (query) => {
      const row = query.state.data;
      return row !== undefined && resource.isComplete(row) ? DETAIL_STALE_MS : 0;
    },
  });
}
